// src/components/chat-bot/CardChat.js
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  IconButton,
  Avatar,
  Typography,
  CircularProgress,
  Tooltip,
  TextField,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
} from "@mui/material";

// Standard MUI Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

// ** Styles
import "./CardChat.css";
import "./TypingIndicator.css";
import BotIcon from "../Assets/Icon/kissanIcon.png";
import useStore from "../../zustand/store";

// ----------------------------------------------------------------------
// Constants
const API_BASE_URL = "https://api.kisan360.net";
const AI_API_URL = "https://ai.uaf.edu.pk/api/chat/completions";

// MessageBubble component remains the same
const MessageBubble = ({ message, currentUser }) => {
  const isSelf = message.isUser;

  // Convert Markdown (***, **, _) to HTML tags
  const formatMessage = (text) => {
    if (!text) return "";
    return text
      .replace(/\*\*\*(.*?)\*\*\*/g, "<b><i>$1</i></b>")
      .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
      .replace(/_(.*?)_/g, "<i>$1</i>")
      .replace(/\n/g, "<br/>");
  };

  return (
    <div className={`chat ${isSelf ? "" : "chat-left"}`}>
      <div className="chat-avatar">
        {isSelf ? (
          <Avatar src={currentUser?.profileImage} />
        ) : (
          <Avatar sx={{ padding: "4px" }}>
            <img
              src={BotIcon}
              alt="Chatbot Icon"
              style={{ height: 42, width: 42, objectFit: "contain" }}
            />
          </Avatar>
        )}
      </div>

      <div className="chat-body">
        <div className="chat-content">
          {message.image && (
            <Box
              component="img"
              src={message.image}
              alt="User upload"
              sx={{
                width: "100%",
                maxWidth: "200px",
                borderRadius: "8px",
                marginBottom: message.message ? "8px" : "0",
                objectFit: "cover",
              }}
            />
          )}

          {message.message && (
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.05rem" },
                whiteSpace: "pre-wrap",
              }}
              dangerouslySetInnerHTML={{
                __html: formatMessage(message.message),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
};

const TypingIndicator = () => (
  <div className="chat chat-left">
    <div className="chat-avatar">
      <Avatar sx={{ padding: "4px" }}>
        <img
          src={BotIcon}
          alt="Chatbot Icon"
          style={{ height: 42, width: 42, objectFit: "contain" }}
        />
      </Avatar>
    </div>
    <div className="chat-body">
      <div className="chat-content">
        <Box className="typing-indicator">
          <span />
          <span />
          <span />
        </Box>
      </div>
    </div>
  </div>
);

// ----------------------------------------------------------------------

const CardChat = ({ onClose }) => {
  const { localuser } = useStore();
  const userId = localuser?.userid;
  const token = localuser?.token;

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [openDialogue, setOpenDialogue] = useState(false)
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState("");

  const currentUser = {
    id: userId || "user1",
    profileImage:
      "https://api-dev-minimal-v510.vercel.app/assets/images/avatar/avatar_25.jpg",
  };

  const initialMessages = [
    {
      _id: "init_1",
      message: "Hi! I can see images too. How can I help?",
      isUser: false,
    },
  ];


  useEffect(() => {
    if (token) {
      fetchChatHistory();
    } else {
      setMessages(initialMessages);
    }
  }, [token]);

  const fetchChatHistory = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/chat/get?limit=20&skip=0`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );


      const msgs = response.data.messages ? response.data.messages.reverse() : [];

      if (msgs.length === 0) {
        setMessages(initialMessages);
      } else {
        const formattedMessages = [];

        msgs.forEach((item) => {
          // 1. User Message
          if (item.userMessage) {
            formattedMessages.push({
              _id: item._id + "_user",
              isUser: true,
              message: item.userMessage.text || "",
              image: item.userMessage.imageUrl
                ? `${API_BASE_URL}${item.userMessage.imageUrl}`
                : null,
              time: item.time,
            });
          }

          // 2. Bot Message
          if (item.botMessage) {
            formattedMessages.push({
              _id: item._id + "_bot",
              isUser: false,
              message: item.botMessage.text || "",
              image: null,
              time: item.time,
            });
          }
        });

        setMessages(formattedMessages);
      }
    } catch (error) {
      console.log("History API Error:", error?.response || error.message);
      // Fallback to initial message if error
      setMessages(initialMessages);
    } finally {
      setIsLoading(false);
    }
  };


  const handleResetChat = async () => {
    // const confirmReset = window.confirm("Are you sure you want to delete this chat history?");
    // if (!confirmReset) return;

    setIsLoading(true);
    try {

      await axios.put(
        `${API_BASE_URL}/chat/delete`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMessages(initialMessages);
      setInputValue("");
      handleRemoveImage();
    } catch (error) {
      console.log("Reset Chat Error:", error?.response || error.message);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageBase64(reader.result.split(",")[1]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current.click();
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageBase64("");
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const saveChatToDB = async ({ userId, userText, botText }) => {
    try {
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("userText", userText);
      formData.append("botText", botText);

      if (selectedFile) {
        formData.append("image", selectedFile);
      }

      await axios.post(`${API_BASE_URL}/chat/send`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.log("Save Chat Error:", err);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() && !imageBase64) return;

    const userText = inputValue.trim();
    const userImageUri = imagePreview;

    // Create local UI message
    const userMessageForState = {
      _id: String(Date.now()),
      message: userText,
      isUser: true,
      image: userImageUri,
    };

    // Chat content for AI API
    const contentArray = [];
    if (userText) {
      contentArray.push({ type: "text", text: userText });
    }
    if (imageBase64) {
      contentArray.push({
        type: "image_url",
        image_url: { url: `data:image/jpeg;base64,${imageBase64}` },
      });
    }

    const apiMessagePayload = {
      role: "user",
      content: contentArray,
    };

    // Add to UI immediately
    setMessages((prev) => [...prev, userMessageForState]);
    setInputValue("");
    // We keep selectedFile in state briefly for saveChatToDB, then clear
    const fileToUpload = selectedFile;
    handleRemoveImage();

    setIsSending(true);
    setIsTyping(true);

    try {
      const payload = {
        model: "llama3.2-vision:11b",
        messages: [apiMessagePayload],
      };

      // 🔥 Call AI API
      const response = await axios.post(AI_API_URL, payload, {
        headers: {
          Authorization: `Bearer sk-48326ba060a642b6bb4cfbde9d6c666a`,
        },
      });

      const botText =
        response.data?.choices?.[0]?.message?.content || "No reply.";

      const botResponse = {
        _id: String(Date.now() + 1),
        message: botText,
        isUser: false,
      };


      setMessages((prev) => [...prev, botResponse]);


      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("userText", userText);
      formData.append("botText", botText);
      if (fileToUpload) {
        formData.append("image", fileToUpload);
      }

      await axios.post(`${API_BASE_URL}/chat/send`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

    } catch (error) {
      console.error("Failed to send message:", error);

      const botErrorText =
        error.response?.data?.error ||
        "Sorry, I couldn't connect to the AI. Please visit again.";

      const errorResponse = {
        _id: String(Date.now() + 1),
        message: botErrorText,
        isUser: false,
      };

      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsSending(false);
      setIsTyping(false);
    }
  };

  const renderChats = () => {
    if (isLoading && messages.length === 0) {
      return (
        <Box sx={{ p: 4, display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <CircularProgress size={30} sx={{ color: "white" }} />
        </Box>
      );
    }
    return messages.map((msg) => (
      <MessageBubble key={msg._id} message={msg} currentUser={currentUser} />
    ));
  };

  return (
    <>
      <Card
        className="chat-widget"
        sx={{
          position: "fixed",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#212930",
          width: "100%",
          height: "100%",
          maxWidth: "100vw",
          maxHeight: "100vh",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 0,
          boxShadow: "none",

          "@media (min-width: 600px)": {
            width: "350px",
            height: "550px",
            maxWidth: "350px",
            maxHeight: "550px",
            top: "auto",
            left: "auto",
            bottom: "110px",
            right: "24px",
            borderRadius: "8px",
            boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <div className="w-100 flex-row-between card-header">
          <div className="flex-row-center gap_10">
            <Avatar sx={{ padding: "4px" }}>
              <img
                src={BotIcon}
                alt="Chatbot Icon"
                style={{ height: 42, width: 42, objectFit: "contain" }}
              />
            </Avatar>

            <div className="flex-col-start">
              <h4 style={{ margin: 0, color: "white" }}>Let&apos;s Chat</h4>
              <Typography variant="body2" sx={{ color: "white", opacity: 0.8 }}>
                Powered by KISAN360
              </Typography>
            </div>
          </div>
          <Box>
            <Tooltip title="Reset Chat History" arrow>
              <IconButton onClick={()=>setOpenDialogue(true)} disabled={isLoading || isSending}>
                {isLoading ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 20, color: "white" }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close" arrow>
              <IconButton onClick={onClose}>
                <CancelIcon sx={{ fontSize: 20, color: "white" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </div>

        <div className="chat-app-window">
          <Box className="user-chats scroll-area">
            <div className="chats">
              {renderChats()}
              {isTyping && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          </Box>

          {imagePreview && (
            <Box sx={{ p: 1, position: "relative", width: "fit-content" }}>
              <Badge
                badgeContent={
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage}
                    sx={{
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                <Box
                  component="img"
                  src={imagePreview}
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: 1,
                    objectFit: "cover",
                  }}
                />
              </Badge>
            </Box>
          )}

          <form
            className="chat-app-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept="image/png, image/jpeg, image/gif"
              multiple
            />
            <IconButton onClick={handleAttachClick} disabled={isSending || isLoading}>
              <AttachFileIcon sx={{ fontSize: 20, color: "#4CAE51" }} />
            </IconButton>

            <div className="input-group-merge me-1 form-send-message w-100">
              <TextField
                name="inputMessage"
                variant="standard"
                fullWidth
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    "& input::placeholder": {
                      color: "white",
                      opacity: 1,
                    },
                    color: "white",
                  },
                }}
                placeholder="Ask a question..."
                autoComplete="off"
              />
            </div>
            <IconButton
              className="send"
              color="primary"
              type="submit"
              disabled={isSending || isLoading}
            >
              {isSending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon sx={{ fontSize: 20, color: "#4CAE51" }} />
              )}
            </IconButton>
          </form>
        </div>
      </Card>
      <Dialog
        open={openDialogue}
        onClose={()=>setOpenDialogue(false)}
        PaperProps={{
          sx: {
            bgcolor: "#212930",
            borderRadius: 5,
            boxShadow:
              "0 14px 12px 0 rgba(0, 0, 0, 0.2), 0 10px 25px 0 rgba(0, 0, 0, 0.19)",
            border: "1px solid #323d49",
          },
        }}
      >
        <DialogTitle sx={{color:"#FFFFFF"}}>
          Delete Chat
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{color:"#FFFFFF"}}>
            Are you sure you want to delete this Chat
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenDialogue(false)}>Cancel</Button>
          <Button onClick={()=>handleResetChat()} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

CardChat.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default CardChat;